import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { AppHeader, BottomNavigation, CreateOptionsMenu, BackButton, Button } from './shared';
import { useUserData, useLogout } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';
import './DeleteAccount.css';

/**
 * Issue #137: Delete Account Multi-Step Flow
 * Step 1: Confirmation question + Questionnaire with reasons (expandable)
 * Step 2: Password entry and info
 * Final confirmation popup
 * Design follows LanguageSettings pattern for consistency
 *
 * Issue #150: Password validation before "weiter" button is enabled
 */
function DeleteAccount() {
  const navigation = useNavigation();
  const { userData, refreshUserData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const { t } = useLanguage();

  // Multi-step state - initialize from URL query parameter
  const getInitialStep = () => {
    const params = new URLSearchParams(window.location.search);
    const stepParam = params.get('step');
    const step = parseInt(stepParam, 10);
    return (step >= 1 && step <= 2) ? step : 1;
  };
  const getInitialShowReasons = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('reasons') === 'true';
  };
  const [currentStep, setCurrentStep] = useState(getInitialStep);
  const [showReasons, setShowReasons] = useState(getInitialShowReasons);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [password, setPassword] = useState('');
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Issue #150: Password validation state
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isValidatingPassword, setIsValidatingPassword] = useState(false);
  const validationTimeoutRef = useRef(null);

  // Issue #151: Password visibility toggle
  const [showPassword, setShowPassword] = useState(false);

  // Update URL when step or showReasons changes (for page refresh persistence)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('step', currentStep.toString());
    if (showReasons) {
      params.set('reasons', 'true');
    } else {
      params.delete('reasons');
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [currentStep, showReasons]);

  // Issue #150: Validate password with debounce
  const validatePassword = useCallback(async (pwd) => {
    if (!pwd.trim()) {
      setIsPasswordValid(false);
      setError('');
      return;
    }

    setIsValidatingPassword(true);
    try {
      const response = await axios.post('/api/user/verify-password', {
        password: pwd,
      });
      if (response.data.valid) {
        setIsPasswordValid(true);
        setError('');
      } else {
        setIsPasswordValid(false);
        setError(t('deleteAccount.wrongPassword'));
      }
    } catch (err) {
      setIsPasswordValid(false);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('deleteAccount.errorGeneric'));
      }
    } finally {
      setIsValidatingPassword(false);
    }
  }, [t]);

  // Issue #150: Debounced password validation
  useEffect(() => {
    // Clear previous timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Reset validation state when password changes
    setIsPasswordValid(false);

    // Only validate if password is not empty
    if (password.trim()) {
      setIsValidatingPassword(true);
      validationTimeoutRef.current = setTimeout(() => {
        validatePassword(password);
      }, 500); // 500ms debounce
    } else {
      setError('');
      setIsValidatingPassword(false);
    }

    // Cleanup on unmount
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [password, validatePassword]);

  // Questionnaire reasons (from design)
  const reasons = [
    { id: 'not_interesting', label: t('deleteAccount.reason1') },
    { id: 'finished_study', label: t('deleteAccount.reason2') },
    { id: 'community_dissatisfied', label: t('deleteAccount.reason3') },
    { id: 'unwanted_contacts', label: t('deleteAccount.reason4') },
    { id: 'other', label: t('deleteAccount.reason5') },
  ];

  const handleReasonToggle = (reasonId) => {
    setSelectedReasons(prev =>
      prev.includes(reasonId)
        ? prev.filter(id => id !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleShowReasons = () => {
    setError('');
    setShowReasons(true);
  };

  const handleNextStep = () => {
    setError('');
    setCurrentStep(prev => prev + 1);
  };

  // Issue #150: Only show popup if password is validated
  const handleShowConfirmPopup = () => {
    if (!isPasswordValid) {
      return;
    }
    setShowConfirmPopup(true);
  };

  const handleConfirmDeletion = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.post('/api/user/request-deletion', {
        password: password,
        reasons: selectedReasons.map(id => reasons.find(r => r.id === id)?.label || id),
      });

      if (response.data.success) {
        // Issue #229: Account is deleted, log user out
        handleLogout();
      }
    } catch (err) {
      setShowConfirmPopup(false);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('deleteAccount.errorGeneric'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <>
      <h2 className="delete-step-title">
        {userData?.first_name}, {t('deleteAccount.step1Title')}
      </h2>
      <p className="delete-step-text">
        {t('deleteAccount.step1Text')}
      </p>

      {!showReasons ? (
        <div className="delete-step-actions">
          <Button
            variant="primary"
            onClick={handleShowReasons}
          >
            {t('deleteAccount.continue')}
          </Button>
        </div>
      ) : (
        <>
          <h2 className="delete-step-title">{t('deleteAccount.step2Title')}</h2>
          <p className="delete-step-text">{t('deleteAccount.step2Text')}</p>

          <div className="reasons-list" role="group" aria-label={t('deleteAccount.step2Title')}>
            {reasons.map((reason) => (
              <label key={reason.id} className="reason-item">
                <input
                  type="checkbox"
                  checked={selectedReasons.includes(reason.id)}
                  onChange={() => handleReasonToggle(reason.id)}
                  className="reason-checkbox"
                />
                <span className="checkbox-custom" aria-hidden="true" />
                <span className="reason-label">{reason.label}</span>
              </label>
            ))}
          </div>

          <div className="delete-step-actions">
            <Button
              variant="primary"
              onClick={handleNextStep}
              disabled={selectedReasons.length === 0}
            >
              {t('deleteAccount.continue')}
            </Button>
          </div>
        </>
      )}
    </>
  );

  const renderStep2 = () => (
    <>
      <h2 className="delete-step-title">{t('deleteAccount.step3Title')}</h2>

      <div className="info-section">
        <p className="info-question-answer">
          <span className="info-question">{t('deleteAccount.personalDataQuestion')}</span>
          <br />
          {t('deleteAccount.personalDataAnswer')}
        </p>
      </div>

      <div className="info-section">
        <p className="info-question-answer">
          <span className="info-question">{t('deleteAccount.contentQuestion')}</span>
          <br />
          {t('deleteAccount.contentAnswer')}
        </p>
      </div>

      <div className="password-section">
        {/* Issue #151: Password input with visibility toggle */}
        <div className={`password-input-wrapper ${error ? 'input-error' : ''}`}>
          <input
            id="delete-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="password-input-field"
            placeholder={t('deleteAccount.passwordPlaceholder')}
            autoComplete="current-password"
            aria-label={t('deleteAccount.passwordPlaceholder')}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? 'password-error' : undefined}
          />
          <button
            type="button"
            className={`visibility-toggle ${showPassword ? 'active' : ''}`}
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? t('a11y.hidePassword') : t('a11y.showPassword')}
            aria-pressed={showPassword}
          >
            <span aria-hidden="true">👁️</span>
          </button>
        </div>
        {error && <p id="password-error" className="error-message" role="alert">{error}</p>}
      </div>

      <div className="delete-step-actions">
        <Button
          variant="primary"
          onClick={handleShowConfirmPopup}
          disabled={!isPasswordValid || isValidatingPassword}
          aria-disabled={!isPasswordValid || isValidatingPassword}
        >
          {isValidatingPassword ? t('deleteAccount.validating') : t('deleteAccount.continue')}
        </Button>
      </div>
    </>
  );

  const renderConfirmPopup = () => (
    <div className="confirm-popup-overlay" onClick={() => setShowConfirmPopup(false)}>
      <div className="confirm-popup" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="confirm-popup-title">{t('deleteAccount.confirmTitle')}</h3>
        <p className="confirm-popup-text">{t('deleteAccount.confirmText')}</p>

        <div className="confirm-popup-actions">
          <Button
            variant="secondary"
            onClick={() => setShowConfirmPopup(false)}
            disabled={isSubmitting}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDeletion}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('deleteAccount.deleting') : t('deleteAccount.confirmDelete')}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="delete-account-page">
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation */}
      <div className="tab-navigation-delete">
        <span className="tab-title">{t('deleteAccount.title')}</span>
      </div>

      {/* Delete Account Content */}
      <main className="delete-account-content">
        {/* Back Button */}
        <BackButton
          onClick={navigation.onNavigateToAccountSettings}
          className="back-button-content"
        />

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
      </main>

      {/* Confirmation Popup */}
      {showConfirmPopup && renderConfirmPopup()}

      <BottomNavigation
        activeTab="profile"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />
    </div>
  );
}

export default DeleteAccount;
